import React from "react";
import { datadogRum } from "@datadog/browser-rum";
import { Button } from "mesh-component-library";
import ModalDialog from "../../modal-dialog";
import { useLocale } from "../../../contexts/LocaleContext";
import { TRANSLATION_CONSTANTS } from "../../../app-consts/translations";
import { connect } from "react-redux";
import { RootState } from "../../../reducers";
import { getChatBotFeatureFlag } from "../../../features";
import {
  getCustomDestinationEnabled,
  getDisplayCode,
  getPartnerPhoneNumber,
  getPassThruEnabled,
} from "../../../selector";
import { logChatIntakeResults, openChatbot } from "../../../action";
import { sanitizePhoneForTelLink } from "../../../utilities/PhoneUtils";

type NoDestinationsFoundModalProps = {
  chatEnabled: boolean;
  closeModal: () => void;
  customDestinationEnabled: boolean;
  displayCode: string;
  isDriveSurePartner: boolean;
  logChatIntakeResults: (message: string) => void;
  openChatbot: () => void;
  passThruEnabled: boolean;
  phoneNumber: string;
  selectHomeAddress: () => void;
};

const Component = ({
  chatEnabled,
  closeModal,
  customDestinationEnabled,
  displayCode,
  isDriveSurePartner,
  logChatIntakeResults,
  openChatbot,
  passThruEnabled,
  phoneNumber,
  selectHomeAddress,
}: NoDestinationsFoundModalProps) => {
  const getTranslatedText = useLocale();

  const callForHelp = () => {
    datadogRum.addAction("Call for help button clicked - No Destinations Found");
    const sanitizedPhone = sanitizePhoneForTelLink(phoneNumber);
    if (sanitizedPhone) {
      window.location.href = `tel:${sanitizedPhone}`;
    } else {
      datadogRum.addError("[NoDestinationFound] Unable to get customer service number for user to call");
    }
  };

  const getModalText = () => {
    if (displayCode === "GM") {
      return "Sorry. Can't find a dealer. Please call customer service.";
    } else if (customDestinationEnabled && !passThruEnabled) {
      return getTranslatedText(TRANSLATION_CONSTANTS.ENTER_MANUAL_LOCATION);
    } else if (isDriveSurePartner) {
      return getTranslatedText(TRANSLATION_CONSTANTS.NO_REPAIR_CENTER_FOUND);
    } else {
      return getContacUsModalParagraph();
    }
  };

  const getContactUsButtonText = () =>
    chatEnabled
      ? getTranslatedText(TRANSLATION_CONSTANTS.CHAT_FOR_HELP)
      : getTranslatedText(TRANSLATION_CONSTANTS.CALL_HELP_NOW);

  const getContacUsModalParagraph = () =>
    chatEnabled
      ? getTranslatedText(TRANSLATION_CONSTANTS.NO_SUGGESTIONS_CHAT_BUTTON)
      : getTranslatedText(TRANSLATION_CONSTANTS.NO_SUGGESTIONS_CALL_CENTER, displayCode);

  const handleContactUsClick = () => {
    if (chatEnabled) {
      handleOpenChat();
    } else {
      callForHelp();
    }
  };

  const handleOpenChat = () => {
    logChatIntakeResults("Chat button clicked from Help Stuck page");
    openChatbot();
  };

  return (
    <ModalDialog
      id="locationError"
      title={getTranslatedText(TRANSLATION_CONSTANTS.NO_REPAIR_CENTERS_FOUND)}
      promptType="error"
      hideTrigger={true}
      isActive={true}
      onClose={closeModal}
    >
      <p>{getModalText()}</p>
      <div className="l-grid">
        {customDestinationEnabled && !passThruEnabled && (
          <div className="l-grid__col l-grid__col--12">
            <a className="c-btn c-btn--primary" onClick={closeModal}>
              <p>{getTranslatedText(TRANSLATION_CONSTANTS.ENTER_LOCATION)}</p>
            </a>
          </div>
        )}
        {isDriveSurePartner && (
          <div className="l-grid__col l-grid__col--12">
            <a className="c-btn c-btn--primary" onClick={selectHomeAddress}>
              <p>{getTranslatedText(TRANSLATION_CONSTANTS.SELECT_HOME_ADDRESS)}</p>
            </a>
          </div>
        )}
        <Button
          size="lg"
          hasUpgrade
          onClick={handleContactUsClick}
          onKeyDown={handleContactUsClick}
          utils={{
            fullWidth: true,
          }}
        >
          {getContactUsButtonText()}
        </Button>
      </div>
    </ModalDialog>
  );
};

const mapStateToProps = (state: RootState) =>
  ({
    chatEnabled: getChatBotFeatureFlag(state),
    customDestinationEnabled: getCustomDestinationEnabled(state),
    displayCode: getDisplayCode(state),
    passThruEnabled: getPassThruEnabled(state),
    phoneNumber: getPartnerPhoneNumber(state),
  } as Pick<
    NoDestinationsFoundModalProps,
    "chatEnabled" | "customDestinationEnabled" | "displayCode" | "passThruEnabled" | "phoneNumber"
  >);

export const NoDestinationsFoundModal = connect(mapStateToProps, {
  logChatIntakeResults,
  openChatbot,
})(Component);
